import { useId, useRef } from "react";
import { MousePointer2, Hand, ZoomIn, ZoomOut, Scan, Focus, Keyboard } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@trillion/ui/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@trillion/ui/components/ui/tooltip";
import { CANVAS_SHORTCUTS, canvasAriaShortcut, canvasShortcutHint, isMacPlatform, type CanvasShortcut } from "../lib/canvas-shortcuts";
import type { CanvasMode, NavigationAction, navigationAvailability } from "../lib/canvas-navigation";

type Props = {
  mode: CanvasMode; temporaryPan: boolean; zoom: number;
  available: ReturnType<typeof navigationAvailability>;
  onModeChange: (mode: CanvasMode) => void; onNavigate: (action: NavigationAction) => void;
  reference: "button" | "canvas" | null; onReferenceChange: (value: "button" | "canvas" | null) => void;
  onReturnFocus: () => void;
};
export function CanvasNavigationControls(props: Props) {
  const descriptionId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const openedFrom = useRef(props.reference);
  if (props.reference) openedFrom.current = props.reference;
  const effective = props.temporaryPan ? "pan" : props.mode;
  const control = (action: CanvasShortcut, icon: React.ReactNode, onClick: () => void, enabled = true, reason?: string, pressed?: boolean) => (
    <Tooltip key={action}><TooltipTrigger asChild><span className="inline-flex" tabIndex={!enabled ? 0 : undefined} aria-describedby={!enabled ? `${descriptionId}-${action}` : undefined}>
      <Button size="sm" variant={pressed ? "secondary" : "ghost"} className="h-8 gap-1 px-2"
        aria-label={CANVAS_SHORTCUTS[action].label} aria-describedby={!enabled ? `${descriptionId}-${action}` : undefined} aria-keyshortcuts={canvasAriaShortcut(action)} aria-pressed={pressed}
        disabled={!enabled} onClick={onClick}>
        {icon}{["select", "pan"].includes(action) && <span>{action === "select" ? "Select" : "Pan"}</span>}
      </Button>
      {!enabled && <span id={`${descriptionId}-${action}`} className="sr-only">{reason}</span>}
    </span></TooltipTrigger><TooltipContent className="max-w-[calc(100vw-2rem)] break-words">
      {CANVAS_SHORTCUTS[action].label} ({canvasShortcutHint(action)}){action === "pan" && " · Hold Space for temporary pan"}
      {!enabled && reason && <p>{reason}</p>}
    </TooltipContent></Tooltip>
  );
  return <TooltipProvider delayDuration={300}>
    <div className="board-navigation" data-canvas-controls role="group" aria-label="Canvas navigation">
      {control("select", <MousePointer2 className="size-4" />, () => props.onModeChange("select"), true, undefined, effective === "select")}
      {control("pan", <Hand className="size-4" />, () => props.onModeChange("pan"), true, undefined, effective === "pan")}
      {props.temporaryPan && <span role="status" className="px-1 text-xs">Space held</span>}
      {([ ["zoomOut", <ZoomOut className="size-4" />], ["actualSize", <span className="min-w-10 tabular-nums">{Math.round(props.zoom * 100)}%</span>],
        ["zoomIn", <ZoomIn className="size-4" />], ["fitBoard", <Scan className="size-4" />], ["fitSelection", <Focus className="size-4" />] ] as const)
        .map(([action, icon]) => control(action, icon, () => props.onNavigate(action), props.available[action].enabled, props.available[action].reason))}
      <Button ref={trigger} size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts (?)" aria-keyshortcuts={canvasAriaShortcut("shortcuts")} onClick={() => props.onReferenceChange("button")}>
        <Keyboard className="size-4" />
      </Button>
    </div>
    <Dialog open={props.reference !== null} onOpenChange={open => { if (!open) props.onReferenceChange(null); }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto" onCloseAutoFocus={event => {
        event.preventDefault();
        if (openedFrom.current === "canvas") props.onReturnFocus(); else trigger.current?.focus({ preventScroll: true });
      }}>
        <DialogHeader><DialogTitle>Keyboard shortcuts</DialogTitle><DialogDescription>
          Canvas shortcuts work while the canvas or a node has focus. Text fields, dialogs and controls keep their native keys.
        </DialogDescription></DialogHeader>
        <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 text-sm">
          {(Object.keys(CANVAS_SHORTCUTS) as CanvasShortcut[]).map(action => <div key={action} className="contents">
            <dt>{CANVAS_SHORTCUTS[action].label}</dt><dd className="text-right"><kbd>{canvasShortcutHint(action)}</kbd>
              {action === "redo" && !isMacPlatform() && <span> / Ctrl+Y</span>}</dd>
          </div>)}
        </dl>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>{isMacPlatform() ? "Command (⌘)" : "Control (Ctrl)"} is the platform modifier. Shift-click or modifier-click adds or toggles selection. Drag blank canvas in Select to fully enclose objects; marquee replaces selection.</p>
          <p>Middle-drag or two-axis wheel/trackpad scroll pans. Pinch or Ctrl-wheel zooms. Tab focuses nodes; Enter selects a focused node and arrow keys move editable nodes in Select (Shift moves farther).</p>
          <p>The bottom editing toolbar contains shape insertion, supported formatting, Clipboard and Organize (group, align, distribute and stack). Editing commands depend on selection, permissions and undo history.</p>
        </div>
      </DialogContent>
    </Dialog>
  </TooltipProvider>;
}
